import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Check, FileText } from "lucide-react";
import { fetchTemplates, createTemplate, updateTemplate, deleteTemplate } from "../../services/crmServices/templateService";

const EMPTY_FORM = { name: "", body: "" };

export function TemplatesManagementModal({ open, onClose }) {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // null = list view, "add" = adding new, templateId = editing existing
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);

    useEffect(() => {
        if (open) {
            loadTemplates();
        }
    }, [open]);

    const loadTemplates = async () => {
        setLoading(true);
        try {
            const res = await fetchTemplates();
            setTemplates(res?.data || []);
        } catch {
            toast.error("Failed to load templates");
        } finally {
            setLoading(false);
        }
    };

    const handleStartAdd = () => {
        setForm(EMPTY_FORM);
        setEditingId("__new__");
    };

    const handleStartEdit = (template) => {
        setForm({ name: template.name, body: template.body });
        setEditingId(template._id);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.error("Template name is required");
            return;
        }
        if (!form.body.trim()) {
            toast.error("Template body is required");
            return;
        }

        setSaving(true);
        try {
            if (editingId === "__new__") {
                const res = await createTemplate({ name: form.name.trim(), body: form.body.trim() });
                if (res?.status === 200) {
                    toast.success("Template created");
                    setTemplates((prev) => [res.data, ...prev]);
                    handleCancelEdit();
                } else {
                    toast.error(res?.message || "Failed to create template");
                }
            } else {
                const res = await updateTemplate({ _id: editingId, name: form.name.trim(), body: form.body.trim() });
                if (res?.status === 200) {
                    toast.success("Template updated");
                    setTemplates((prev) => prev.map((t) => (t._id === editingId ? res.data : t)));
                    handleCancelEdit();
                } else {
                    toast.error(res?.message || "Failed to update template");
                }
            }
        } catch {
            toast.error("Failed to save template");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (template) => {
        if (!window.confirm(`Delete template "${template.name}"?`)) return;
        try {
            const res = await deleteTemplate(template._id);
            if (res?.status === 200) {
                toast.success("Template deleted");
                setTemplates((prev) => prev.filter((t) => t._id !== template._id));
                if (editingId === template._id) handleCancelEdit();
            } else {
                toast.error(res?.message || "Failed to delete template");
            }
        } catch {
            toast.error("Failed to delete template");
        }
    };

    const isEditing = editingId !== null;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) { handleCancelEdit(); onClose(); } }}>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
                <DialogHeader className="border-b pb-3">
                    <DialogTitle className="flex items-center text-lg font-semibold text-gray-900">
                        <FileText className="h-5 w-5 mr-2 text-blue-600" />
                        Comment Templates Management
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto">
                    {/* Add / Edit form */}
                    {isEditing ? (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg m-4">
                            <h3 className="text-sm font-semibold text-blue-800 mb-3">
                                {editingId === "__new__" ? "New Template" : "Edit Template"}
                            </h3>
                            <div className="space-y-3">
                                <div>
                                    <Label className="text-xs font-medium text-gray-700">Template Name</Label>
                                    <Input
                                        value={form.name}
                                        onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                                        placeholder="e.g. Conversion Template"
                                        className="h-9 mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-medium text-gray-700">Template Body</Label>
                                    <Textarea
                                        value={form.body}
                                        onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
                                        placeholder="Enter the template text. Use {{firstName}}, {{lastName}}, {{email}}, {{phone}}, {{country}}, {{balance}} as dynamic placeholders."
                                        rows={10}
                                        className="mt-1 font-mono text-sm resize-y"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Placeholders: <code className="bg-gray-100 px-1 rounded">&#123;&#123;firstName&#125;&#125;</code>{" "}
                                        <code className="bg-gray-100 px-1 rounded">&#123;&#123;lastName&#125;&#125;</code>{" "}
                                        <code className="bg-gray-100 px-1 rounded">&#123;&#123;email&#125;&#125;</code>{" "}
                                        <code className="bg-gray-100 px-1 rounded">&#123;&#123;phone&#125;&#125;</code>{" "}
                                        <code className="bg-gray-100 px-1 rounded">&#123;&#123;country&#125;&#125;</code>{" "}
                                        <code className="bg-gray-100 px-1 rounded">&#123;&#123;balance&#125;&#125;</code>{" "}
                                        <code className="bg-gray-100 px-1 rounded">&#123;&#123;agentName&#125;&#125;</code>
                                    </p>
                                </div>
                                <div className="flex gap-2 pt-1">
                                    <Button
                                        onClick={handleSave}
                                        disabled={saving}
                                        size="sm"
                                        className="bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                        <Check className="h-3.5 w-3.5 mr-1.5" />
                                        {saving ? "Saving..." : "Save Template"}
                                    </Button>
                                    <Button onClick={handleCancelEdit} variant="outline" size="sm">
                                        <X className="h-3.5 w-3.5 mr-1.5" />
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="p-4">
                            <Button
                                onClick={handleStartAdd}
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white mb-4"
                            >
                                <Plus className="h-3.5 w-3.5 mr-1.5" />
                                Add Template
                            </Button>

                            {loading ? (
                                <div className="text-center py-10 text-gray-500 text-sm">Loading templates...</div>
                            ) : templates.length === 0 ? (
                                <div className="text-center py-10 text-gray-400">
                                    <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
                                    <p className="font-medium">No templates yet</p>
                                    <p className="text-xs mt-1">Click "Add Template" to create your first one</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {templates.map((template) => (
                                        <div
                                            key={template._id}
                                            className="border border-gray-200 rounded-lg p-3 bg-white hover:border-blue-300 transition-colors"
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-semibold text-gray-800 truncate">
                                                        {template.name}
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 whitespace-pre-wrap font-mono">
                                                        {template.body}
                                                    </p>
                                                </div>
                                                <div className="flex gap-1 flex-shrink-0">
                                                    <button
                                                        onClick={() => handleStartEdit(template)}
                                                        className="p-1.5 rounded text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(template)}
                                                        className="p-1.5 rounded text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="border-t pt-3 px-4 pb-4 flex justify-end">
                    <Button variant="outline" size="sm" onClick={() => { handleCancelEdit(); onClose(); }}>
                        Close
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
